import { useState, useEffect, useCallback } from 'react'

export default function CmsGallery({ slug = 'klinik-atmosfaere', className = '' }) {
  const [gallery, setGallery] = useState(null)
  const [loading, setLoading] = useState(true)
  const [lightboxIndex, setLightboxIndex] = useState(null)

  useEffect(() => {
    let isMounted = true
    async function fetchGallery() {
      try {
        setLoading(true)
        const res = await fetch('/_api/public/content/galleries')
        if (!res.ok) throw new Error('Kunne ikke hente galleri')
        const data = await res.json()
        const items = Array.isArray(data) ? data : data.items || []
        const matched = items.find((g) => {
          const itemSlug = typeof g.slug === 'object' ? g.slug?._ : g.slug
          return itemSlug === slug
        })
        if (isMounted) {
          setGallery(matched || items[0] || null)
        }
      } catch (err) {
        console.error('Fejl ved indlæsning af galleri:', err)
      } finally {
        if (isMounted) setLoading(false)
      }
    }
    fetchGallery()
    return () => {
      isMounted = false
    }
  }, [slug])

  const images = gallery?.images || []

  // Lightbox keyboard navigation
  const handleKeyDown = useCallback(
    (e) => {
      if (lightboxIndex === null) return
      if (e.key === 'Escape') setLightboxIndex(null)
      if (e.key === 'ArrowRight') {
        setLightboxIndex((prev) => (prev + 1) % images.length)
      }
      if (e.key === 'ArrowLeft') {
        setLightboxIndex((prev) => (prev - 1 + images.length) % images.length)
      }
    },
    [lightboxIndex, images.length]
  )

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handleKeyDown])

  if (loading) {
    return (
      <div className="py-8 text-center text-sm text-[#7D6476] animate-pulse">
        Indlæser galleri...
      </div>
    )
  }

  if (!gallery || images.length === 0) {
    return null
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {gallery.title && (
        <div className="text-center max-w-xl mx-auto mb-6">
          <h3 className="font-serif text-2xl sm:text-3xl font-medium text-[#2B3824]">
            {gallery.title}
          </h3>
          {gallery.description && (
            <p className="text-sm text-[#6C5667] mt-1.5">
              {gallery.description}
            </p>
          )}
        </div>
      )}

      {/* Grid of gallery photos */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 sm:gap-4">
        {images.map((imgUrl, idx) => (
          <button
            key={idx}
            type="button"
            onClick={() => setLightboxIndex(idx)}
            className="group relative aspect-4/3 w-full overflow-hidden rounded-xl sm:rounded-2xl border border-[#DFE6D5] bg-[#F6F8F3] shadow-xs cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#556B48]"
            aria-label={`Forstør billede ${idx + 1}`}
          >
            <img
              src={`/cdn-cgi/image/width=600,quality=80,format=auto/${imgUrl.replace(/^https:\/\/[^/]+/, '')}`}
              alt={`${gallery.title} - Foto ${idx + 1}`}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
              loading="lazy"
              onError={(e) => {
                // Fallback to direct URL if cdn-cgi fails
                e.currentTarget.src = imgUrl
              }}
            />
            <div className="absolute inset-0 bg-[#222E1D]/0 transition-colors group-hover:bg-[#222E1D]/20 flex items-center justify-center">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-[#3F5236] opacity-0 transition-opacity duration-200 group-hover:opacity-100 shadow-md">
                <i className="fa-solid fa-expand text-sm" aria-hidden="true" />
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Lightbox Modal */}
      {lightboxIndex !== null && (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-[#192215]/90 backdrop-blur-sm p-4 sm:p-8"
          onClick={() => setLightboxIndex(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-5xl overflow-hidden rounded-2xl bg-[#F6F8F3] p-2 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              type="button"
              onClick={() => setLightboxIndex(null)}
              className="absolute right-4 top-4 z-10 flex h-10 w-10 items-center justify-center rounded-full bg-white/80 text-[#2B3824] shadow-md hover:bg-white focus:outline-none"
              aria-label="Luk forstørrelse"
            >
              <i className="fa-solid fa-xmark text-base" aria-hidden="true" />
            </button>

            {/* Prev button */}
            <button
              type="button"
              onClick={() =>
                setLightboxIndex((prev) => (prev - 1 + images.length) % images.length)
              }
              className="absolute left-4 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/80 text-[#2B3824] shadow-md hover:bg-white focus:outline-none"
              aria-label="Forrige billede"
            >
              <i className="fa-solid fa-chevron-left text-base" aria-hidden="true" />
            </button>

            {/* Next button */}
            <button
              type="button"
              onClick={() =>
                setLightboxIndex((prev) => (prev + 1) % images.length)
              }
              className="absolute right-4 top-1/2 -translate-y-1/2 z-10 flex h-11 w-11 items-center justify-center rounded-full bg-white/80 text-[#2B3824] shadow-md hover:bg-white focus:outline-none"
              aria-label="Næste billede"
            >
              <i className="fa-solid fa-chevron-right text-base" aria-hidden="true" />
            </button>

            {/* Image display */}
            <img
              src={images[lightboxIndex]}
              alt={`${gallery.title} - stor visning`}
              className="max-h-[75vh] w-auto max-w-full rounded-xl object-contain mx-auto select-none"
            />

            <div className="py-2 px-4 text-center text-xs text-[#6F5B6A] flex items-center justify-between">
              <span>{gallery.title}</span>
              <span>
                {lightboxIndex + 1} af {images.length}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
